/**
 * AlimtalkBillsCard.tsx
 * 우리 매장 카카오 알림톡 사용료 — 원장이 자기 화면에서 직접 본다.
 *
 * 🔴 왜 따로 있나
 * 옆의 '청구서 조회'(BillDetailView)는 매장에 '플랜'이 할당돼 있어야 금액을 계산하는데,
 * 실제로는 어느 매장에도 플랜이 없어서 원장이 열면 "플랜이 할당되어 있지 않습니다"만
 * 떴다. 정작 본부가 청구하는 근거는 따로 있었고 원장은 그걸 볼 방법이 없어 매달
 * 메일을 기다려야 했다. 이 카드는 그 실제 청구서를 그대로 보여준다.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlimtalkBill,
  billingUsageRepository,
} from '../../repositories/BillingUsageRepository';

const won = (n: number) => (n || 0).toLocaleString('ko-KR') + '원';
const cnt = (n: number) => (n || 0).toLocaleString('ko-KR');

/** 기한이 지났는가 — 오늘(KST) 날짜와 문자열로 비교한다.
 *  Date 로 파싱하면 브라우저 시간대에 따라 하루 어긋난다. */
const isOverdue = (due: string | null): boolean => {
  if (!due) return false;
  const todayKST = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
  return due < todayKST;
};

const monthLabel = (ym: string) => {
  const [y, m] = (ym || '').split('-');
  return y && m ? `${y}년 ${Number(m)}월` : ym;
};

/** 상태는 원장 입장의 말로 바꿔 보여준다 — 'unbilled' 같은 말을 그대로 두지 않는다. */
const STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  unbilled: { label: '안내 전', bg: '#f3f4f6', fg: '#4b5563' },
  billed: { label: '입금 대기', bg: '#fef3c7', fg: '#92400e' },
  paid: { label: '입금 완료', bg: '#d1fae5', fg: '#065f46' },
  waived: { label: '면제', bg: '#e0e7ff', fg: '#3730a3' },
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e5e7eb',
    marginBottom: '24px',
  },
  title: { fontSize: '18px', fontWeight: 700, color: '#1a1a1a', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#6b7280', marginBottom: '20px' },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  month: { fontSize: '15px', fontWeight: 600, color: '#111827' },
  count: { fontSize: '13px', color: '#6b7280' },
  amount: { fontSize: '18px', fontWeight: 700, color: '#111827' },
  badge: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: '999px',
  },
  detail: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '16px',
    marginTop: '-4px',
    marginBottom: '14px',
  },
  detailHead: { fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '10px' },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#4b5563',
    padding: '4px 0',
  },
  sumRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    padding: '6px 0',
    borderTop: '1px solid #e5e7eb',
    marginTop: '8px',
  },
  account: {
    marginTop: '14px',
    padding: '12px 14px',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
  },
  accountLabel: { fontSize: '12px', color: '#1e40af', fontWeight: 600, marginBottom: '4px' },
  dueLine: { fontSize: '13px', color: '#1e40af', marginTop: '8px', fontWeight: 600 },
  dueOver: { fontSize: '13px', color: '#b91c1c', marginTop: '8px', fontWeight: 700 },
  accountValue: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#1e3a8a',
    // 계좌번호는 원장이 그대로 긁어 쓸 수 있어야 한다
    userSelect: 'all' as const,
  },
  note: { fontSize: '12px', color: '#6b7280', marginTop: '12px', lineHeight: 1.6 },
  empty: { fontSize: '14px', color: '#6b7280', padding: '20px 0', textAlign: 'center' as const },
};

const AlimtalkBillsCard: React.FC = () => {
  const [bills, setBills] = useState<AlimtalkBill[]>([]);
  const [account, setAccount] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { bills: rows, paymentAccount } = await billingUsageRepository.getAlimtalkBills(12);
      setBills(rows);
      setAccount(paymentAccount);
      // 가장 최근 달은 펼쳐서 보여준다 — 원장이 제일 궁금한 것이다
      if (rows.length > 0) setOpen(rows[0].year_month);
    } catch (e) {
      setError('사용료 내역을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>카카오 알림톡 사용료</div>
        <div style={styles.empty}>불러오는 중…</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.title}>카카오 알림톡 사용료</div>
      <div style={styles.subtitle}>
        학부모님께 실제로 발송된 알림톡 건수입니다. 발송에 실패한 건은 청구하지 않습니다.
      </div>

      {error && <div style={{ ...styles.empty, color: '#b91c1c' }}>{error}</div>}

      {!error && bills.length === 0 && (
        <div style={styles.empty}>아직 청구된 사용료가 없습니다.</div>
      )}

      {bills.map((b) => {
        const st = STATUS[b.status] || { label: b.status, bg: '#f3f4f6', fg: '#4b5563' };
        const expanded = open === b.year_month;
        return (
          <React.Fragment key={b.year_month}>
            <div
              style={styles.row}
              onClick={() => setOpen(expanded ? null : b.year_month)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setOpen(expanded ? null : b.year_month);
              }}
            >
              <div>
                <div style={styles.month}>
                  {monthLabel(b.year_month)}{' '}
                  <span style={{ ...styles.badge, backgroundColor: st.bg, color: st.fg }}>
                    {st.label}
                  </span>
                </div>
                <div style={styles.count}>발송 {cnt(b.sent_count)}건 · 건당 {b.unit_price}원</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={styles.amount}>{won(b.total_amount)}</div>
                <div style={styles.count}>{expanded ? '접기 ▲' : '내역 보기 ▼'}</div>
              </div>
            </div>

            {expanded && (
              <div style={styles.detail}>
                <div style={styles.detailHead}>어떤 알림이 나갔나</div>
                {b.breakdown.length === 0 && (
                  <div style={styles.detailRow}>
                    <span>내역 없음</span>
                  </div>
                )}
                {b.breakdown.map((t) => (
                  <div key={t.code} style={styles.detailRow}>
                    <span>{t.label}</span>
                    <span>{cnt(t.count)}건</span>
                  </div>
                ))}

                <div style={styles.sumRow}>
                  <span>청구 대상 (발송 성공)</span>
                  <span>{cnt(b.billable_count)}건</span>
                </div>
                {b.free_quota > 0 && (
                  <div style={styles.detailRow}>
                    <span>무료 제공</span>
                    <span>{cnt(b.free_quota)}건</span>
                  </div>
                )}
                <div style={styles.detailRow}>
                  <span>공급가액</span>
                  <span>{won(b.supply_amount)}</span>
                </div>
                <div style={styles.detailRow}>
                  <span>부가세 (10%)</span>
                  <span>{won(b.vat_amount)}</span>
                </div>
                <div style={{ ...styles.sumRow, fontWeight: 700, color: '#111827' }}>
                  <span>합계</span>
                  <span>{won(b.total_amount)}</span>
                </div>

                {/* 금액만 보여주면 원장이 어디로 보내야 할지 모른다.
                    이미 입금·면제된 달에는 보여줄 이유가 없어 숨긴다.
                    계좌가 서버에 설정돼 있지 않으면 줄 자체가 안 나온다 —
                    틀린 계좌를 보여주느니 안 보이는 게 낫다. */}
                {account && b.status !== 'paid' && b.status !== 'waived' && (
                  <div style={styles.account}>
                    <div style={styles.accountLabel}>입금 계좌</div>
                    <div style={styles.accountValue}>{account}</div>
                    {/* 기한이 없으면(=아직 안내 전) 줄을 만들지 않는다.
                        없는 기한을 지어내면 원장이 잘못된 날짜를 믿는다. */}
                    {b.due_date && (
                      <div style={isOverdue(b.due_date) ? styles.dueOver : styles.dueLine}>
                        입금 기한: {b.due_date}
                        {isOverdue(b.due_date) ? ' (기한 지남)' : ' 까지'}
                      </div>
                    )}
                  </div>
                )}
                {/* 계좌가 서버에 설정돼 있지 않으면 위 블록이 통째로 안 나온다.
                    그래도 기한은 알려준다 — 원장이 언제까지인지는 알아야 한다. */}
                {!account && b.due_date && b.status !== 'paid' && b.status !== 'waived' && (
                  <div style={isOverdue(b.due_date) ? styles.dueOver : styles.dueLine}>
                    입금 기한: {b.due_date}
                    {isOverdue(b.due_date) ? ' (기한 지남)' : ' 까지'}
                  </div>
                )}

                {b.failed_count > 0 && (
                  <div style={styles.note}>
                    ※ 이 달에 발송 실패 {cnt(b.failed_count)}건이 있었습니다.{' '}
                    <strong>실패한 건은 청구하지 않았습니다.</strong>
                  </div>
                )}
                {b.paid_at && (
                  <div style={styles.note}>
                    입금 확인일: {new Date(b.paid_at).toLocaleDateString('ko-KR', {
                      timeZone: 'Asia/Seoul',
                    })}
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default AlimtalkBillsCard;
